import { useQuery } from '@tanstack/react-query'

import { api } from '../../api/client'
import { formatDateTime } from '../../lib/format'
import { marketSituation, useEpisodes, useLatestRun } from '../live'
import { MARKET_LABEL, MARKET_PLAIN, MARKET_TONE } from '../vocab'
import { Term, TermIf } from '../term'
import { BrainGate, Card, NotConnected, Tag, inr } from '../ui'
import { QueryNotice } from '../PageState'
import type { BrainSector } from '../../api/types'
import { SortTh, useSort } from '../sort'

const ROTATION: Record<string, string> = {
  leading: 'Leading — stronger for 1 and 3 months',
  improving: 'Improving — stronger this month',
  weakening: 'Weakening — slipping this month',
  lagging: 'Lagging — weaker for 1 and 3 months',
  unknown: 'Not enough history yet',
}

const REGIME_PLAIN: Record<string, string> = {
  bullish: 'Above its own trend — a rising market.',
  bearish: 'Below its own trend — a falling market.',
  unknown: 'Not enough data to judge the trend.',
}

export default function Market() {
  return (
    <BrainGate>
      <MarketBody />
    </BrainGate>
  )
}

function MarketBody() {
  const latest = useLatestRun()
  const episodes = useEpisodes()
  const regime = useQuery({ queryKey: ['marketRegime'], queryFn: api.marketRegime })

  const run = latest.data!
  const situation = marketSituation(run)
  const { sorted: sectors, sorter } = useSort(run.sectors ?? [], {
    rank: (s: BrainSector) => s.rank,
    sector: (s: BrainSector) => s.name,
    direction: (s: BrainSector) => ROTATION[s.rotation] ?? s.rotation,
    strength: (s: BrainSector) => s.strength_20d,
  }, { key: null }, { rank: 'asc', sector: 'asc', direction: 'asc' })

  return (
    <div className="tm-page tm-grid">
      <div className="tm-grid tm-cols-3">
        <Card
          glow
          className="tm-span-2"
          title={<Term k="market_mode">Market Mode</Term>}
          sub={`From the brain's run on ${formatDateTime(run.started_at)}`}
        >
          <div className="tm-hero-state" style={{ minWidth: 200 }}>
            <div>
              <div className="tm-state-word" style={{ fontSize: '1.7rem' }}>
                {run.banner.mode ? <TermIf k={run.banner.mode}>{MARKET_LABEL[run.banner.mode] ?? run.banner.mode}</TermIf> : 'Unknown'}
              </div>
              {run.banner.mode && (
                <Tag tone={MARKET_TONE[run.banner.mode] ?? 'blue'}>{MARKET_PLAIN[run.banner.mode]}</Tag>
              )}
            </div>
          </div>
          {run.banner.headline && <p className="tm-note">{run.banner.headline}</p>}
          {situation && situation.label !== 'unlabelled' ? (
            <p className="tm-note">
              Situation: {situation.label}
              {situation.is_unknown ? ' — never seen before' : ''}
              {situation.evidence.length > 0 ? ` — ${situation.evidence.join('; ')}` : ''}
            </p>
          ) : (
            <p className="tm-dim">The brain did not recognise a named market situation today.</p>
          )}
        </Card>

        <Card title="NIFTY 50" sub="From the market data service">
          <QueryNotice q={regime} what="the NIFTY level" />
          {regime.data && (
            <>
              <div className="tm-big tm-num" style={{ fontSize: '1.6rem' }}>
                {regime.data.nifty_close != null ? `${inr(regime.data.nifty_close, 0)} pts` : '—'}
              </div>
              <p className="tm-note">{REGIME_PLAIN[regime.data.regime] ?? REGIME_PLAIN.unknown}</p>
              <p className="tm-dim" style={{ fontSize: '0.78rem' }}>
                Volatility: {regime.data.volatility_level}
              </p>
            </>
          )}
        </Card>
      </div>

      <div className="tm-grid tm-cols-3">
        <Card className="tm-span-2" title="Sector Rotation" sub="Rank among all sectors, last 20 trading days">
          {sectors.length === 0 ? (
            <p className="tm-dim">No sector data in this run.</p>
          ) : (
            <div className="tm-table-wrap" tabIndex={0} role="region" aria-label="Table, scroll sideways to see more">
              <table className="tm-table">
                <thead>
                  <tr>
                    <SortTh sorter={sorter} k="rank">Rank</SortTh>
                    <SortTh sorter={sorter} k="sector">Sector</SortTh>
                    <SortTh sorter={sorter} k="direction">Direction</SortTh>
                    <SortTh sorter={sorter} k="strength" right>20-day strength</SortTh>
                  </tr>
                </thead>
                <tbody>
                  {sectors.map((s) => (
                    <tr key={s.sector}>
                      <td className="tm-num">{s.rank}/{s.of_total}</td>
                      <td className="tm-strong">{s.name}</td>
                      <td className="tm-dim">{ROTATION[s.rotation] ?? s.rotation}</td>
                      <td className="tm-right tm-num">
                        {s.strength_20d == null ? '—' : `${s.strength_20d >= 0 ? '+' : ''}${(s.strength_20d * 100).toFixed(1)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <NotConnected
          what="Market Breadth"
          reason="How many stocks rose vs fell today is not connected yet — no endpoint reports it."
        />
      </div>

      <div className="tm-grid tm-cols-2">
        <Card title="Market History" sub="The stretches the market went through, as the brain names them">
          <QueryNotice q={episodes} what="market history" />
          {episodes.data && episodes.data.length === 0 && <p className="tm-dim">No history recorded yet.</p>}
          {episodes.data && episodes.data.length > 0 && (
            <div className="tm-table-wrap" tabIndex={0} role="region" aria-label="Table, scroll sideways to see more">
              <table className="tm-table">
                <thead>
                  <tr>
                    <th>Situation</th>
                    <th>From</th>
                    <th>To</th>
                    <th className="tm-right">NIFTY change</th>
                  </tr>
                </thead>
                <tbody>
                  {episodes.data.map((e) => (
                    <tr key={e.start_day}>
                      <td>{e.label}</td>
                      <td className="tm-dim">{e.start_day}</td>
                      <td className="tm-dim">{e.end_day ?? 'still going'}</td>
                      <td className="tm-right tm-num">
                        {e.nifty_change == null ? '—' : `${e.nifty_change >= 0 ? '+' : ''}${(e.nifty_change * 100).toFixed(1)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <NotConnected
          what="Fear Gauge (India VIX)"
          reason="The brain does not read the VIX index yet — only a volatility level, shown on the NIFTY card."
        />
      </div>
    </div>
  )
}
